/* ai-answer-dialog.tsx generated by @compiled/babel-plugin v0.38.1 */
import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import "./ai-answer-dialog.compiled.css";
import { ax, ix } from "@compiled/react/runtime";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import React, { memo, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { bind } from 'bind-event-listener';
import isEqual from 'lodash/isEqual';
import { useIntl } from 'react-intl-next';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import FeatureGates from '@atlaskit/feature-gate-js-client';
import { fg } from '@atlaskit/platform-feature-flags';
import { Box } from '@atlaskit/primitives/compiled';
import UFOCustomCohortData from '@atlaskit/react-ufo/custom-cohort-data';
import UFOCustomData from '@atlaskit/react-ufo/custom-data';
import UFOSegment from '@atlaskit/react-ufo/segment';
import { AIEventsInstrumentationProvider, useAIEventsInstrumentation } from '@atlassian/ai-analytics';
import { useActivityClient } from '@atlassian/recent-work-client';
import { deleteSessionItem, getSessionItem, SearchDialog, setSessionItem } from '@atlassian/search-dialog';
import { SAIN_USAGE_DWELL_THRESHOLD, SainAction, useSAINUsageCriteriaMet } from '../common/controller/analytics';
import { useSessionId } from '../common/controller/analytics/helpers';
import { useDwellTimer } from '../common/utils';
import { getSAINLayerFrontendExperiment } from '../common/utils/get-sain-layer-frontend-experiment';
import { getUnifiedAIExperienceName } from '../common/utils/get-unified-ai-experience-name';
import { AiBrand, CardTemplate, DEFAULT_GAS_CHANNEL, QueryIntent } from '../constants';
import { ErrorBoundary } from '../error-boundary';
import { KnowledgeCard } from '../knowledge-cards';
import { fetchAgentCardData } from '../knowledge-cards/gql/hydrate-agent-card/utils';
import { fetchPersonKnowledgeCardDataWithPostFiltering } from '../knowledge-cards/gql/hydrate-people-card/utils';
import { fetchTeamKnowledgeCardDataWithRanking } from '../knowledge-cards/gql/hydrate-team-card';
import { fetchTopicCardData } from '../knowledge-cards/gql/hydrate-topic-card/utils';
import { getAnswerCardType, sha256Hash } from '../utils';
import { SmartAnswerCard as SmartAnswerCardWithTabs } from '../v2/smart-answer-card/smart-answer-card';
import { SmartAnswerCardConsolidated } from '../v2/smart-answer-card/smart-answer-card-consolidated';
import { SmartAnswerCardDynamicLayout } from '../v2/smart-answer-card/smart-answer-card-dynamic-layout';
import { AIAnswerContext } from './ai-answer-context';
import { AIAnswerDefaultLoadingWrapper } from './ai-answer-loading/styled';
import { onAIAnswerDialogDroppedOff, onAIAnswerDialogHidden, onAIAnswerDialogQueryIntent, onAIAnswerDialogUnmount, onAIAnswerDialogViewed, onAIAnswerNotShown, onAIAnswerResultShown, onSmartAnswerRouteDetection } from './analytics';
import { DefinitionAnswerCard } from './definition-answer-card/definition-answer-card';
import { aiAnswerDialogMetric, MarkAIAnswerDialogMetricEnd } from './metrics';
import { LegacySmartAnswerCard } from './smart-answer-card/legacy-smart-answer-card';
import { LegacySmartAnswerCardWithoutStreamingExpand } from './smart-answer-card/legacy-smart-answer-card-without-streaming-expand';
import { SmartAnswerCard } from './smart-answer-card/smart-answer-card';
import { SmartAnswerCardWithoutStreamingExpand } from './smart-answer-card/smart-answer-card-without-streaming-expand';
import { NLPSearchCustomDefinitionType, NLPSearchErrorState } from './types';
import { fetchAIAnswer } from './utils/fetchAIAnswer';
import { getKnowledgeCardResultCount } from './utils/knowledgecard-utils';
var styles = {
  root: "_kqswh2mm"
};
export var SEARCH_AI_ANSWER_CACHE_KEY = 'atlassian.search-dialog.ai-answer-component';
export var DEFAULT_EMPTY_NLP_SEARCH_STATE = {
  errorState: NLPSearchErrorState.Default,
  format: null,
  disclaimer: null,
  nlpResults: [],
  uniqueSources: []
};
var SearchAIDialogComponent = function SearchAIDialogComponent(_ref) {
  var _nlpSearch$nlpResults, _nlpSearch$nlpFollowU;
  var query = _ref.query,
    setQuery = _ref.setQuery,
    additionalQueryContext = _ref.additionalQueryContext,
    cloudId = _ref.cloudId,
    orgId = _ref.orgId,
    getAIAnswer = _ref.getAIAnswer,
    onNavigate = _ref.onNavigate,
    onClose = _ref.onClose,
    advancedSearchUrl = _ref.advancedSearchUrl,
    searchSessionId = _ref.searchSessionId,
    userDetails = _ref.userDetails,
    wrapper = _ref.wrapper,
    loadingWrapper = _ref.loadingWrapper,
    _ref$source = _ref.source,
    source = _ref$source === void 0 ? 'unknown' : _ref$source,
    _ref$isReadingAids = _ref.isReadingAids,
    isReadingAids = _ref$isReadingAids === void 0 ? false : _ref$isReadingAids,
    readingAidsOptions = _ref.readingAidsOptions,
    _ref$extraAnalyticsAt = _ref.extraAnalyticsAttributes,
    extraAnalyticsAttributes = _ref$extraAnalyticsAt === void 0 ? {} : _ref$extraAnalyticsAt,
    cardProviderProps = _ref.cardProviderProps,
    _ref$baseQuery = _ref.baseQuery,
    baseQuery = _ref$baseQuery === void 0 ? '' : _ref$baseQuery,
    onLoad = _ref.onLoad,
    followUpsEnabled = _ref.followUpsEnabled,
    selectedFilters = _ref.selectedFilters,
    parsedFilters = _ref.parsedFilters,
    selectedProduct = _ref.selectedProduct,
    mappedProductFilter = _ref.mappedProductFilter,
    answerLoadingRef = _ref.answerLoadingRef,
    smartAnswersRoute = _ref.smartAnswersRoute,
    _ref$cardTemplate = _ref.cardTemplate,
    cardTemplate = _ref$cardTemplate === void 0 ? CardTemplate.DEFAULT : _ref$cardTemplate,
    sourceProduct = _ref.sourceProduct,
    workspaceId = _ref.workspaceId,
    _ref$brand = _ref.brand,
    brand = _ref$brand === void 0 ? AiBrand.AI : _ref$brand,
    closeParentDialog = _ref.closeParentDialog,
    callbacks = _ref.callbacks,
    isAdminHubAIEnabled = _ref.isAdminHubAIEnabled,
    definitionToggleEnabled = _ref.definitionToggleEnabled,
    showChatFollowUps = _ref.showChatFollowUps,
    _ref$showGenericFoote = _ref.showGenericFooter,
    showGenericFooter = _ref$showGenericFoote === void 0 ? false : _ref$showGenericFoote,
    _ref$initiallyExpande = _ref.initiallyExpanded,
    initiallyExpanded = _ref$initiallyExpande === void 0 ? false : _ref$initiallyExpande,
    experimentContext = _ref.experimentContext;
  var intl = useIntl();
  var locale = intl.locale;
  var _useState = useState(true),
    _useState2 = _slicedToArray(_useState, 2),
    loading = _useState2[0],
    setLoading = _useState2[1];
  var _useState3 = useState(false),
    _useState4 = _slicedToArray(_useState3, 2),
    dwellTracked = _useState4[0],
    setDwellTracked = _useState4[1];
  var dwellTrackedRef = useRef(false);
  var partialResponseAbortControllerRef = useRef();
  var _useState5 = useState(),
    _useState6 = _slicedToArray(_useState5, 2),
    partialResponse = _useState6[0],
    setPartialResponse = _useState6[1];
  var _useState7 = useState(),
    _useState8 = _slicedToArray(_useState7, 2),
    queryReasoning = _useState8[0],
    setQueryReasoning = _useState8[1];
  var _useState9 = useState([]),
    _useState0 = _slicedToArray(_useState9, 2),
    dynamicLayoutSubAgentMessages = _useState0[0],
    setDynamicLayoutSubAgentMessages = _useState0[1];
  var _useState1 = useState(true),
    _useState10 = _slicedToArray(_useState1, 2),
    tabHasFocus = _useState10[0],
    setTabHasFocus = _useState10[1];
  var _useState11 = useState(null),
    _useState12 = _slicedToArray(_useState11, 2),
    dwellTimer = _useState12[0],
    setDwellTimer = _useState12[1];
  var dwellTimerRef = useRef(null);
  var _useState13 = useState(false),
    _useState14 = _slicedToArray(_useState13, 2),
    answerStreamed = _useState14[0],
    setAnswerStreamed = _useState14[1];
  var _useState15 = useState(false),
    _useState16 = _slicedToArray(_useState15, 2),
    isFMPReached = _useState16[0],
    setIsFMPReached = _useState16[1];
  var _useState17 = useState(false),
    _useState18 = _slicedToArray(_useState17, 2),
    isFmpInViewport = _useState18[0],
    setIsFmpInViewport = _useState18[1];
  var _useState19 = useState(initiallyExpanded),
    _useState20 = _slicedToArray(_useState19, 2),
    isAnswerExpanded = _useState20[0],
    setIsAnswerExpanded = _useState20[1];

  // FMP-based dwell timer for analytics sizing
  if (fg('kd_fmp_dwell_observer')) {
    // fg check is stable between renders and won't cause issues with hook calls
    // eslint-disable-next-line react-hooks/rules-of-hooks
    useDwellTimer({
      isEnabled: isFMPReached,
      isInViewport: isFmpInViewport,
      tabHasFocus: tabHasFocus,
      thresholdMs: SAIN_USAGE_DWELL_THRESHOLD,
      onDwellStart: function onDwellStart() {
        fireSAINUsageEvent({
          metCriteria: isAnswerExpanded ? SainAction.FMP_DWELL_START_EXPANDED : SainAction.FMP_DWELL_START,
          dwellTime: 0,
          commonAttributes: commonAttributes
        });
      },
      onDwellThreshold: function onDwellThreshold(dwellTime) {
        fireSAINUsageEvent({
          metCriteria: SainAction.FMP_DWELL_REACHED,
          dwellTime: dwellTime,
          commonAttributes: commonAttributes,
          sourceProduct: sourceProduct
        });
      },
      onDwellEnd: function onDwellEnd(dwellTime) {
        fireSAINUsageEvent({
          metCriteria: SainAction.FMP_RESULT_EXITED,
          dwellTime: dwellTime,
          commonAttributes: commonAttributes,
          sourceProduct: sourceProduct
        });
      }
    });
  }
  var _useState21 = useState(undefined),
    _useState22 = _slicedToArray(_useState21, 2),
    liked = _useState22[0],
    setLiked = _useState22[1];
  var _useState23 = useState(false),
    _useState24 = _slicedToArray(_useState23, 2),
    likesDisabled = _useState24[0],
    setLikesDisabled = _useState24[1];
  var _useState25 = useState(undefined),
    _useState26 = _slicedToArray(_useState25, 2),
    initialReported = _useState26[0],
    setInitialReported = _useState26[1];
  var _useState27 = useState(DEFAULT_EMPTY_NLP_SEARCH_STATE),
    _useState28 = _slicedToArray(_useState27, 2),
    nlpSearch = _useState28[0],
    setData = _useState28[1];
  var filtersRef = useRef(selectedFilters);
  var selectedProductRef = useRef(selectedProduct);
  var _useAnalyticsEvents = useAnalyticsEvents(),
    createAnalyticsEvent = _useAnalyticsEvents.createAnalyticsEvent;
  var _useSAINUsageCriteria = useSAINUsageCriteriaMet(),
    fireSAINUsageEvent = _useSAINUsageCriteria.fireSAINUsageEvent;
  var resultRef = useRef(null);
  var initialLoadTs = useRef(Date.now()).current;
  var loadedTsRef = useRef();
  var fmpLoadedTsRef = useRef();
  var dwellTimeRef = useRef();
  var sainReverAllExperiment = FeatureGates.getExperimentValue('sain_revert_all_experiment', 'enable', false);
  var kdSainTabsExperienceRevert = FeatureGates.getExperimentValue('kd_sain_tabs_experience_revert', 'enable', false);
  var _getSAINLayerFrontend = getSAINLayerFrontendExperiment({
      abTestId: 'kd_sain_smooth_streaming_non_hello_exp'
    }),
    kdSainSmoothStreamingExperiment = _getSAINLayerFrontend.serveExperiment;
  var _getSAINLayerFrontend2 = getSAINLayerFrontendExperiment({
      abTestId: 'kd_dynamic_layout_in_sain_exp'
    }),
    kdDynamicLayoutInSainExperiment = _getSAINLayerFrontend2.serveExperiment;
  var shouldServeTabsExperiment = sainReverAllExperiment || kdSainTabsExperienceRevert;
  var markLoadedTs = function markLoadedTs() {
    loadedTsRef.current = Date.now();
    return loadedTsRef.current - initialLoadTs;
  };
  var fmpMarkLoadedTs = function fmpMarkLoadedTs() {
    if (fmpLoadedTsRef.current) {
      return fmpLoadedTsRef.current - initialLoadTs;
    }
  };
  var startViewportDwell = function startViewportDwell() {
    dwellTimeRef.current = Date.now();
  };
  var markViewportDwell = function markViewportDwell() {
    if (dwellTimeRef.current) {
      return Date.now() - dwellTimeRef.current;
    }
    return 0;
  };
  var _useAIEventsInstrumen = useAIEventsInstrumentation(),
    trackAIInteractionInit = _useAIEventsInstrumen.trackAIInteractionInit,
    trackAIInteractionDismiss = _useAIEventsInstrumen.trackAIInteractionDismiss;
  var sessionId = useSessionId({
    userId: userDetails.id || '',
    cloudId: cloudId,
    source: source
  });
  var activityClient = useActivityClient('v3', 'people');
  var isAnswered = !loading && nlpSearch.errorState === null;
  var hasError = !loading && !!nlpSearch.errorState && nlpSearch.errorState !== NLPSearchErrorState.Default;
  var answerString = (_nlpSearch$nlpResults = nlpSearch.nlpResults) !== null && _nlpSearch$nlpResults !== void 0 && _nlpSearch$nlpResults.length ? nlpSearch.nlpResults[0].nlpResult : nlpSearch.nlpResult || '';
  var answerFormat = nlpSearch.format;
  var followUps = (_nlpSearch$nlpFollowU = nlpSearch.nlpFollowUpResults) === null || _nlpSearch$nlpFollowU === void 0 ? void 0 : _nlpSearch$nlpFollowU.followUps;
  var errorStateSuggestedQueries = nlpSearch.nlpSuggestedQueries;
  var sources = nlpSearch.uniqueSources;
  var disclaimer = nlpSearch.disclaimer;
  var answerEditor = nlpSearch.nlpResultEditor;
  var extraAPIAnalyticsAttributes = nlpSearch.extraAPIAnalyticsAttributes;
  var answerCardType = getAnswerCardType(isReadingAids, smartAnswersRoute);
  var commonAttributes = useMemo(function () {
    return {
      cloudId: cloudId,
      orgId: orgId !== null && orgId !== void 0 ? orgId : '',
      queryHash: sha256Hash(query),
      baseQueryHash: sha256Hash(baseQuery),
      query: query,
      appliedFilters: parsedFilters,
      searchSessionId: searchSessionId,
      source: source,
      answerString: answerString,
      answerLength: answerString.length,
      answerWordCount: answerString.split(/\s+/).length,
      answerFormat: answerFormat,
      answerCardType: answerCardType,
      sources: sources,
      followUps: followUps || [],
      errorStateSuggestedQueries: errorStateSuggestedQueries || [],
      errorState: nlpSearch.errorState,
      errorCode: nlpSearch.errorCode,
      extraAttributes: _objectSpread(_objectSpread({
        featureGates: {
          precomputedDefinitionsEnabled: fg('kd_enable_precomputed_definitions')
        }
      }, extraAnalyticsAttributes), extraAPIAnalyticsAttributes),
      apiSource: 'assistance-service',
      workspaceId: workspaceId !== null && workspaceId !== void 0 ? workspaceId : '',
      sessionId: sessionId,
      brand: brand
    };
  }, [cloudId, orgId, query, baseQuery, parsedFilters, searchSessionId, source, answerString, answerFormat, nlpSearch.errorState, nlpSearch.errorCode, extraAnalyticsAttributes, extraAPIAnalyticsAttributes, workspaceId, sessionId, brand, answerCardType, followUps, sources, errorStateSuggestedQueries]);
  var fireAnalyticsEvent = useCallback(function (payload) {
    createAnalyticsEvent(payload).fire(DEFAULT_GAS_CHANNEL);
  }, [createAnalyticsEvent]);
  var onNavigateWithMouseEvent = useCallback(function (event) {
    if (!event.ctrlKey && !event.metaKey) {
      onNavigate();
    }
  }, [onNavigate]);
  var getAIAnswerFn = useCallback(function () {
    switch (smartAnswersRoute) {
      case QueryIntent.PERSON:
        return function (_args) {
          return fetchPersonKnowledgeCardDataWithPostFiltering({
            tenantId: cloudId,
            personQuery: query,
            sourceProduct: sourceProduct,
            workspaceId: workspaceId !== null && workspaceId !== void 0 ? workspaceId : ''
          });
        };
      case QueryIntent.TEAM:
        return function (_args) {
          return fetchTeamKnowledgeCardDataWithRanking({
            activityClient: activityClient,
            orgId: orgId !== null && orgId !== void 0 ? orgId : '',
            siteId: cloudId,
            teamQuery: query,
            sourceProduct: sourceProduct,
            workspaceId: workspaceId !== null && workspaceId !== void 0 ? workspaceId : ''
          });
        };
      case QueryIntent.TOPIC:
        return function (_args) {
          return fetchTopicCardData({
            cloudId: cloudId,
            query: query,
            workspaceId: workspaceId !== null && workspaceId !== void 0 ? workspaceId : '',
            sourceProduct: sourceProduct
          });
        };
      case QueryIntent.AGENT:
        return function () {
          return fetchAgentCardData({
            query: query,
            sourceProduct: sourceProduct !== null && sourceProduct !== void 0 ? sourceProduct : '',
            limit: 7,
            cloudId: cloudId,
            workspaceId: workspaceId !== null && workspaceId !== void 0 ? workspaceId : ''
          });
        };
      case QueryIntent.JIRA_NATURAL_LANGUAGE_QUERY:
        // data fetching is handled by the JiraNlToJqlCard internally,
        // this is to prevent streaming from being called
        return null;
      case QueryIntent.ORG_CHART:
        return function () {
          return fetchPersonKnowledgeCardDataWithPostFiltering({
            tenantId: cloudId,
            personQuery: query,
            sourceProduct: sourceProduct,
            workspaceId: workspaceId !== null && workspaceId !== void 0 ? workspaceId : ''
          });
        };
      default:
      // fallthrough to getAIAnswer
    }
    return getAIAnswer;
  }, [getAIAnswer, smartAnswersRoute, query, orgId, cloudId, activityClient, sourceProduct, workspaceId]);
  var updateContextCache = useCallback(function (_ref2) {
    var collapsed = _ref2.collapsed,
      liked = _ref2.liked,
      reported = _ref2.reported,
      recentWorkListExpanded = _ref2.recentWorkListExpanded,
      sourcesExpanded = _ref2.sourcesExpanded,
      fromFollowUp = _ref2.fromFollowUp;
    var currentCache = JSON.parse(getSessionItem(SEARCH_AI_ANSWER_CACHE_KEY) || '{}');
    setSessionItem(SEARCH_AI_ANSWER_CACHE_KEY, JSON.stringify(_objectSpread({
      query: query,
      additionalContext: additionalQueryContext || '',
      data: nlpSearch,
      collapsed: collapsed === undefined ? currentCache.collapsed : collapsed,
      liked: liked === undefined ? currentCache.liked : liked,
      reported: reported === undefined ? currentCache.reported : reported,
      fromFollowUp: fromFollowUp === undefined ? currentCache.fromFollowUp : fromFollowUp,
      recentWorkListExpanded: recentWorkListExpanded === undefined ? currentCache.recentWorkListExpanded : recentWorkListExpanded,
      sourcesExpanded: sourcesExpanded === undefined ? currentCache.sourcesExpanded : sourcesExpanded
    }, fg('kd_fix_sain_mau_instrumentation') ? {
      extraAPIAnalyticsAttributes: {
        requestId: extraAPIAnalyticsAttributes === null || extraAPIAnalyticsAttributes === void 0 ? void 0 : extraAPIAnalyticsAttributes.requestId
      }
    } : {})));
  }, [query, additionalQueryContext, nlpSearch, extraAPIAnalyticsAttributes === null || extraAPIAnalyticsAttributes === void 0 ? void 0 : extraAPIAnalyticsAttributes.requestId]);
  var onFMPReached = useCallback(function () {
    setIsFMPReached(true);
    fmpLoadedTsRef.current = Date.now();
  }, []);
  var onAnswerExpandedChange = useCallback(function (expanded) {
    setIsAnswerExpanded(expanded);
  }, []);
  useEffect(function () {
    if (answerLoadingRef) {
      answerLoadingRef.current = !!loading;
    }
  }, [loading, answerLoadingRef]);
  useEffect(function () {
    aiAnswerDialogMetric.start();
    // Reset FMP dwell tracking for new queries
    setIsFMPReached(false);
    setIsFmpInViewport(false);
    if (dwellTimer) {
      clearTimeout(dwellTimer);
      setDwellTimer(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query]);
  var handleIntersect = function handleIntersect(entries) {
    entries.forEach(function (entry) {
      if (!loading && (isAnswered || hasError)) {
        if (entry.isIntersecting) {
          startViewportDwell();
          fireAnalyticsEvent(onAIAnswerDialogViewed(_objectSpread({}, commonAttributes)));
          if (fg('kd_fix_sain_mau_instrumentation')) {
            if (smartAnswersRoute !== QueryIntent.TEAM && smartAnswersRoute !== QueryIntent.PERSON && !dwellTrackedRef.current) {
              dwellTimerRef.current = setTimeout(function () {
                dwellTrackedRef.current = true;
                fireSAINUsageEvent({
                  metCriteria: SainAction.DWELL_REACHED,
                  dwellTime: markViewportDwell(),
                  commonAttributes: commonAttributes
                });
              }, SAIN_USAGE_DWELL_THRESHOLD);
            }
          } else if (smartAnswersRoute !== QueryIntent.TEAM && smartAnswersRoute !== QueryIntent.PERSON && !dwellTracked) {
            var usageTimer = setTimeout(function () {
              setDwellTracked(true);
              fireSAINUsageEvent({
                metCriteria: SainAction.DWELL_REACHED,
                dwellTime: markViewportDwell(),
                commonAttributes: commonAttributes
              });
            }, SAIN_USAGE_DWELL_THRESHOLD);
            setDwellTimer(usageTimer);
          }
        } else {
          fireAnalyticsEvent(onAIAnswerDialogHidden(_objectSpread({}, commonAttributes), markViewportDwell()));
          if (dwellTimerRef.current && fg('kd_fix_sain_mau_instrumentation')) {
            clearTimeout(dwellTimerRef.current);
            dwellTimerRef.current = null;
          }
        }
      }
    });
  };
  useLayoutEffect(function () {
    var observer = new IntersectionObserver(handleIntersect, {
      threshold: 0.3
    });
    if (resultRef.current && tabHasFocus) {
      observer.observe(resultRef.current);
    }
    return function () {
      observer === null || observer === void 0 || observer.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, resultRef, isAnswered, hasError, tabHasFocus]);

  // FMP dwell intersection observer
  useLayoutEffect(function () {
    // Use different thresholds based on expanded state:
    // - Collapsed: 1.0 (100% in viewport)
    // - Expanded: 0.3 (30% in viewport)
    var threshold = isAnswerExpanded ? 0.3 : 1.0;
    var fmpObserver = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (isFMPReached) {
          // Always update viewport state when FMP is reached, regardless of tab focus
          // Tab focus is handled separately in the dwell timer hook
          setIsFmpInViewport(entry.isIntersecting);
        }
      });
    }, {
      threshold: threshold
    });
    if (resultRef.current && isFMPReached) {
      fmpObserver.observe(resultRef.current);
    }
    return function () {
      fmpObserver === null || fmpObserver === void 0 || fmpObserver.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resultRef, isFMPReached, isAnswerExpanded]);
  useEffect(function () {
    var cached = JSON.parse(getSessionItem(SEARCH_AI_ANSWER_CACHE_KEY) || '{}');
    if ((cached === null || cached === void 0 ? void 0 : cached.query) === query && (cached === null || cached === void 0 ? void 0 : cached.additionalContext) === additionalQueryContext) {
      setData(cached.data);
      if (cached.liked !== undefined) {
        setLiked(cached.liked);
        // if a like is cached, disable likes
        setLikesDisabled(true);
      }
      if (cached.reported !== undefined) {
        setInitialReported(cached.reported);
      }
      setLoading(false);
    } else {
      deleteSessionItem(SEARCH_AI_ANSWER_CACHE_KEY);
      setLoading(true);
      setLiked(undefined);
      setLikesDisabled(false);
      fetchAIAnswer({
        cloudId: cloudId,
        locale: locale,
        query: query,
        additional_context: additionalQueryContext,
        source: source,
        followUpsEnabled: followUpsEnabled && !cached.fromFollowUp,
        getAIAnswer: getAIAnswerFn(),
        setLoading: setLoading,
        partialResponse: partialResponse,
        partialResponseAbortControllerRef: partialResponseAbortControllerRef,
        setPartialResponse: setPartialResponse,
        setData: setData,
        setAnswerStreamed: setAnswerStreamed,
        parsedFilters: parsedFilters,
        selectedProduct: mappedProductFilter,
        isReadingAids: isReadingAids,
        fireAnalyticsEvent: fireAnalyticsEvent,
        setQueryReasoning: setQueryReasoning,
        setDynamicLayoutSubAgentMessages: setDynamicLayoutSubAgentMessages,
        experimentContext: experimentContext
      });
    }
    filtersRef.current = selectedFilters;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, additionalQueryContext]);
  useEffect(function () {
    if (!isEqual(filtersRef.current, selectedFilters) || !isEqual(selectedProductRef.current, selectedProduct)) {
      deleteSessionItem(SEARCH_AI_ANSWER_CACHE_KEY);
      setLoading(true);
      setLiked(undefined);
      setLikesDisabled(false);
      fetchAIAnswer({
        cloudId: cloudId,
        locale: locale,
        query: query,
        source: source,
        followUpsEnabled: followUpsEnabled,
        getAIAnswer: getAIAnswerFn(),
        setLoading: setLoading,
        partialResponse: partialResponse,
        partialResponseAbortControllerRef: partialResponseAbortControllerRef,
        setPartialResponse: setPartialResponse,
        setData: setData,
        setAnswerStreamed: setAnswerStreamed,
        parsedFilters: parsedFilters,
        selectedProduct: mappedProductFilter,
        isReadingAids: isReadingAids,
        fireAnalyticsEvent: fireAnalyticsEvent,
        setQueryReasoning: setQueryReasoning,
        setDynamicLayoutSubAgentMessages: setDynamicLayoutSubAgentMessages,
        experimentContext: experimentContext
      });
    }
    filtersRef.current = selectedFilters;
    selectedProductRef.current = selectedProduct;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFilters, selectedProduct]);
  useEffect(function () {
    if (isAnswered) {
      var _callbacks$onAIAnswer2;
      // if a person or team query returns no data don't log result shown
      var numAddtlWhoMatches = smartAnswersRoute === QueryIntent.PERSON ? getKnowledgeCardResultCount({
        answerString: answerString
      }) : 0;
      if ((smartAnswersRoute === QueryIntent.PERSON || smartAnswersRoute === QueryIntent.TEAM) && getKnowledgeCardResultCount({
        answerString: answerString
      }) < 1) {
        var _callbacks$onAIAnswer;
        markLoadedTs();
        callbacks === null || callbacks === void 0 || (_callbacks$onAIAnswer = callbacks.onAIAnswerAnalyticsFired) === null || _callbacks$onAIAnswer === void 0 || _callbacks$onAIAnswer.call(callbacks);
        return;
      }
      fireAnalyticsEvent(onAIAnswerResultShown(_objectSpread(_objectSpread({}, commonAttributes), {}, {
        isStreamed: answerStreamed,
        loadTime: markLoadedTs(),
        fmpLoadTime: fmpMarkLoadedTs(),
        extraAttributes: _objectSpread(_objectSpread({}, commonAttributes.extraAttributes), {}, {
          multipleUserResultsShown: numAddtlWhoMatches > 0,
          numAddtlWhoMatches: numAddtlWhoMatches,
          isCuratedDefinition: !!answerEditor,
          isPrecomputedDefinition: nlpSearch.customDefinitionType === NLPSearchCustomDefinitionType.PRECOMPUTED
        })
      })));
      if (numAddtlWhoMatches > 0) {
        fireSAINUsageEvent({
          commonAttributes: commonAttributes
        });
      }
      callbacks === null || callbacks === void 0 || (_callbacks$onAIAnswer2 = callbacks.onAIAnswerAnalyticsFired) === null || _callbacks$onAIAnswer2 === void 0 || _callbacks$onAIAnswer2.call(callbacks);
    } else if (hasError) {
      var _callbacks$onAIAnswer3;
      fireAnalyticsEvent(onAIAnswerNotShown(_objectSpread(_objectSpread({}, commonAttributes), {}, {
        isStreamed: answerStreamed,
        loadTime: markLoadedTs(),
        fmpLoadTime: fmpMarkLoadedTs()
      })));
      callbacks === null || callbacks === void 0 || (_callbacks$onAIAnswer3 = callbacks.onAIAnswerAnalyticsFired) === null || _callbacks$onAIAnswer3 === void 0 || _callbacks$onAIAnswer3.call(callbacks);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, isAnswered]);
  useLayoutEffect(function () {
    trackAIInteractionInit();
    fireAnalyticsEvent(onAIAnswerDialogQueryIntent(commonAttributes));
    if (!isReadingAids && query && smartAnswersRoute) {
      fireAnalyticsEvent(onSmartAnswerRouteDetection(commonAttributes, smartAnswersRoute));
    }
    return function () {
      fireAnalyticsEvent(onAIAnswerDialogHidden(_objectSpread({}, commonAttributes), markViewportDwell()));
      fireAnalyticsEvent(onAIAnswerDialogUnmount(_objectSpread(_objectSpread({}, commonAttributes), {}, {
        isReadingAids: isReadingAids,
        sourceProduct: sourceProduct,
        dwellTime: loadedTsRef.current ? Date.now() - loadedTsRef.current : 0,
        loadTime: markLoadedTs(),
        fmpLoadTime: fmpMarkLoadedTs()
      })));
      trackAIInteractionDismiss();
      if (!loadedTsRef.current) {
        fireAnalyticsEvent(onAIAnswerDialogDroppedOff(_objectSpread({}, commonAttributes)));
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(function () {
    var handleWindowBlurOrClose = function handleWindowBlurOrClose() {
      if (tabHasFocus && !loading && (isAnswered || hasError)) {
        fireAnalyticsEvent(onAIAnswerDialogHidden(_objectSpread({}, commonAttributes), markViewportDwell()));
        if (dwellTimerRef.current && fg('kd_fix_sain_mau_instrumentation')) {
          clearTimeout(dwellTimerRef.current);
          dwellTimerRef.current = null;
        }
      }
      setTabHasFocus(false);
    };
    var handleWindowFocus = function handleWindowFocus() {
      setTabHasFocus(true);
      if (!dwellTrackedRef.current && !dwellTimerRef.current && fg('kd_fix_sain_mau_instrumentation')) {
        dwellTimerRef.current = setTimeout(function () {
          dwellTrackedRef.current = true;
          fireSAINUsageEvent({
            metCriteria: SainAction.DWELL_REACHED,
            dwellTime: markViewportDwell(),
            commonAttributes: commonAttributes
          });
        }, SAIN_USAGE_DWELL_THRESHOLD);
      }
    };
    var removeBlurListener = bind(window, {
      type: 'blur',
      listener: handleWindowBlurOrClose
    });
    var removeFocusListener = bind(window, {
      type: 'focus',
      listener: handleWindowFocus
    });
    var removeCloseListener = bind(window, {
      type: 'beforeunload',
      listener: handleWindowBlurOrClose
    });
    return function () {
      removeBlurListener();
      removeFocusListener();
      removeCloseListener();
      if (fg('kd_fix_sain_mau_instrumentation')) {
        if (dwellTimerRef.current) {
          clearTimeout(dwellTimerRef.current);
          dwellTimerRef.current = null;
        }
      } else if (dwellTimer) {
        clearTimeout(dwellTimer);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tabHasFocus, loading]);
  useLayoutEffect(function () {
    if (onLoad && !loading && (isAnswered || hasError)) {
      onLoad();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, isAnswered, hasError]);
  var AIDialogWrapper = wrapper ? wrapper : SearchDialog;
  var AILoadingWrapper = loadingWrapper ? loadingWrapper : AIAnswerDefaultLoadingWrapper;
  var renderDefinition = function renderDefinition() {
    return /*#__PURE__*/React.createElement(DefinitionAnswerCard, null);
  };

  // SAIN in FPS
  var renderSearchPageSmartAnswer = function renderSearchPageSmartAnswer() {
    if ((smartAnswersRoute === QueryIntent.NATURAL_LANGUAGE_QUERY || smartAnswersRoute === QueryIntent.KEYWORD_OR_ACRONYM || smartAnswersRoute === QueryIntent.EXPLICIT_QUESTION) && shouldServeTabsExperiment) {
      return /*#__PURE__*/React.createElement(AIDialogWrapper, null, fg('kd_dynamic_layout_in_sain') || kdDynamicLayoutInSainExperiment ? /*#__PURE__*/React.createElement(SmartAnswerCardDynamicLayout, {
        onFMPReached: onFMPReached,
        onAnswerExpandedChange: onAnswerExpandedChange,
        queryIntent: smartAnswersRoute
      }) : fg('kd_smooth_streaming_fg') || kdSainSmoothStreamingExperiment ? /*#__PURE__*/React.createElement(SmartAnswerCardConsolidated, {
        onFMPReached: onFMPReached,
        onAnswerExpandedChange: onAnswerExpandedChange,
        queryIntent: smartAnswersRoute
      }) : /*#__PURE__*/React.createElement(SmartAnswerCardWithTabs, {
        onFMPReached: onFMPReached,
        onAnswerExpandedChange: onAnswerExpandedChange,
        queryIntent: smartAnswersRoute
      }));
    } else if (fg('kd_sain_enable_expand_when_streaming')) {
      return /*#__PURE__*/React.createElement(AIDialogWrapper, null, /*#__PURE__*/React.createElement(SmartAnswerCard, {
        onFMPReached: onFMPReached,
        onAnswerExpandedChange: onAnswerExpandedChange
      }));
    } else {
      return /*#__PURE__*/React.createElement(AIDialogWrapper, null, /*#__PURE__*/React.createElement(SmartAnswerCardWithoutStreamingExpand, {
        onFMPReached: onFMPReached,
        onAnswerExpandedChange: onAnswerExpandedChange
      }));
    }
  };
  var renderSearchPageKnowledgeCard = function renderSearchPageKnowledgeCard() {
    return /*#__PURE__*/React.createElement(AIDialogWrapper, null, /*#__PURE__*/React.createElement(KnowledgeCard, {
      onFMPReached: onFMPReached
    }));
  };

  // Quick search and advanced search
  var renderDefaultSmartAnswer = function renderDefaultSmartAnswer() {
    return fg('kd_sain_enable_expand_when_streaming') ? /*#__PURE__*/React.createElement(LegacySmartAnswerCard, {
      LoadingWrapper: AILoadingWrapper,
      DialogWrapper: AIDialogWrapper,
      onFMPReached: onFMPReached,
      onAnswerExpandedChange: onAnswerExpandedChange
    }) : /*#__PURE__*/React.createElement(LegacySmartAnswerCardWithoutStreamingExpand, {
      LoadingWrapper: AILoadingWrapper,
      DialogWrapper: AIDialogWrapper,
      onFMPReached: onFMPReached,
      onAnswerExpandedChange: onAnswerExpandedChange
    });
  };
  var renderExperience = function renderExperience() {
    switch (smartAnswersRoute) {
      case QueryIntent.NATURAL_LANGUAGE_QUERY:
      case QueryIntent.KEYWORD_OR_ACRONYM:
      case QueryIntent.EXPLICIT_QUESTION:
      case QueryIntent.DATE:
      case QueryIntent.LOCATION:
        return renderSearchPageSmartAnswer();
      case QueryIntent.PERSON:
      case QueryIntent.TEAM:
      case QueryIntent.TOPIC:
      case QueryIntent.AGENT:
      case QueryIntent.JIRA_NATURAL_LANGUAGE_QUERY:
      case QueryIntent.ORG_CHART:
        return renderSearchPageKnowledgeCard();
      default:
        return isReadingAids ? renderDefinition() : renderDefaultSmartAnswer();
    }
  };
  return /*#__PURE__*/React.createElement(AIAnswerContext.Provider, {
    value: {
      loading: loading,
      partialResponse: partialResponse,
      isAnswered: isAnswered,
      hasError: hasError,
      advancedSearchUrl: advancedSearchUrl,
      userDetails: userDetails,
      disclaimer: disclaimer,
      onNavigate: onNavigateWithMouseEvent,
      onClose: onClose,
      updateContextCache: updateContextCache,
      fireAnalyticsEvent: fireAnalyticsEvent,
      setQuery: setQuery,
      setData: setData,
      commonAttributes: commonAttributes,
      liked: liked,
      setLiked: setLiked,
      likesDisabled: likesDisabled,
      setLikesDisabled: setLikesDisabled,
      initialReported: initialReported,
      isReadingAids: isReadingAids,
      dynamicLayoutSubAgentMessages: dynamicLayoutSubAgentMessages,
      setDynamicLayoutSubAgentMessages: setDynamicLayoutSubAgentMessages,
      readingAidsOptions: readingAidsOptions,
      cardProviderProps: cardProviderProps,
      baseQuery: baseQuery,
      cardTemplate: cardTemplate,
      smartAnswersRoute: smartAnswersRoute,
      brand: brand,
      answerEditor: answerEditor,
      closeParentDialog: closeParentDialog,
      additionalQueryContext: additionalQueryContext,
      callbacks: callbacks,
      isAdminHubAIEnabled: isAdminHubAIEnabled,
      definitionToggleEnabled: definitionToggleEnabled,
      sourceProduct: sourceProduct,
      showChatFollowUps: showChatFollowUps,
      queryReasoning: queryReasoning,
      showGenericFooter: showGenericFooter !== null && showGenericFooter !== void 0 ? showGenericFooter : false,
      initiallyExpanded: initiallyExpanded !== null && initiallyExpanded !== void 0 ? initiallyExpanded : false
    }
  }, /*#__PURE__*/React.createElement(Box, {
    xcss: styles.root,
    ref: resultRef
  }, renderExperience()), !loading && (isAnswered || hasError) && /*#__PURE__*/React.createElement(MarkAIAnswerDialogMetricEnd, null));
};
export var SearchAIDialog = /*#__PURE__*/memo(function (props) {
  var definitionExperienceName = props.definitionExperienceName,
    isReadingAids = props.isReadingAids,
    source = props.source,
    sourceProduct = props.sourceProduct,
    sourceSubProduct = props.sourceSubProduct,
    smartAnswersRoute = props.smartAnswersRoute,
    userGeneratedAI = props.userGeneratedAI;
  return /*#__PURE__*/React.createElement(UFOSegment, {
    name: "search-ai-dialog"
  }, fg('platform_ufo_custom_cohort_data_migration') ? /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(UFOCustomCohortData, {
    dataKey: "hasSAINComponent",
    value: true
  }), /*#__PURE__*/React.createElement(UFOCustomCohortData, {
    dataKey: "sainExperienceName",
    value: getUnifiedAIExperienceName(smartAnswersRoute)
  })) : /*#__PURE__*/React.createElement(UFOCustomData, {
    data: {
      hasSAINComponent: true,
      sainExperienceName: getUnifiedAIExperienceName(smartAnswersRoute)
    }
  }), /*#__PURE__*/React.createElement(ErrorBoundary, props, /*#__PURE__*/React.createElement(AIEventsInstrumentationProvider, {
    config: {
      product: sourceProduct !== null && sourceProduct !== void 0 ? sourceProduct : 'unknown',
      subproduct: sourceSubProduct !== null && sourceSubProduct !== void 0 ? sourceSubProduct : 'unknown',
      aiFeatureName: isReadingAids ? 'aiDefinitions' : 'smartAnswers',
      aiExperienceName: isReadingAids ? definitionExperienceName : getUnifiedAIExperienceName(smartAnswersRoute),
      proactiveGeneratedAI: userGeneratedAI ? 0 : 1,
      userGeneratedAI: userGeneratedAI ? 1 : 0,
      isAIFeature: 1,
      source: source,
      channel: DEFAULT_GAS_CHANNEL
    }
  }, /*#__PURE__*/React.createElement(SearchAIDialogComponent, props))));
});